import * as Sentry from '@sentry/react';
import React, { useContext, useEffect, useState } from "react";
import { Grid, LoadingSpinner } from "../../../../../styles/orderform.tsx";
import { Address as AddressType, AddressPayload, MeteringPointCheckObject, MeteringPointCheckResponse, PostalCodeArea, Street, AddressStatus } from "../../types.ts";

import { styled } from 'styled-components';
import TextInput from "../b2b/TextInput.tsx";

import iconCheck from "../../../../../assets/images/icon-check.svg";
import iconAlertCircle from "../../../../../assets/images/icon-alert-circle-black.svg";
import { useTranslation } from 'react-i18next';
import { OrderContext } from '../../OrderContext.tsx';


const AutoCompleteSelect = styled.div.attrs(() => ({ className: 'autocomplete-container' }))`
  background-color: var(--Valo);
  max-height: 220px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 4px;
  position: absolute;
  z-index: 1;
  width: calc(100% - 2px); // 2px is the border width
  border: solid 1px black;
  max-width: 430px;

  div {
    padding: 4px 10px;
  }

  div:nth-child(even) {
    background-color: #fff;
  }

  div.active, div:hover {
    background-color: var(--b2b-Lime, #E6F499);
  }

`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
  margin: 8px 0;
`;

const StatusBox = styled.div`
  display: flex;
  margin: 20px 0;

  .specify {

  }

  .not-found {
    display: flex;
    background: var(--Valo);
    padding: 8px;
    border-radius: 4px;
    gap: 8px;
    border: 2px solid transparent;

    &.invalid {
      border-color: var(--Punainen);
    }

    .content {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .title {
        font-weight: 600;
      }
    }
  }

  .success {
    border-radius: 4px;
    background: var(--b2b-Lime, #E6F499);
    padding: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const LetterAndResidence = styled.div`
  display:grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  position: relative;

  .autocomplete-container {
    width: calc(50% - 6px);
  }
`;

interface AddressProps {
  streetName: string;
  houseNumber: string;
  zipCode: string;
  cityName: string;
  houseLetter: string;
  residence: string;
  onUpdate: (address?: Partial<AddressType>, status?: AddressStatus) => void;
  careOf?: string;
  zipCodeError?: string;
  cityNameError?: string;
  streetNameError?: string;
  houseNumberError?: string;
  houseLetterError?: string;
  residenceError?: string;
  disableAddressCheck?: boolean;
  hidecareOf?: boolean;
  status?: AddressStatus;
  statusError?: string;
  onlyAddressCheck?: boolean;
}

interface ResidenceData {
  [key: string]: string[]
}

function Address(props: AddressProps) {

  const {
    streetName, houseNumber, zipCode, cityName, houseLetter, residence,
    zipCodeError, cityNameError, streetNameError, houseNumberError, houseLetterError, residenceError,
    careOf, hidecareOf = false, status, statusError, onlyAddressCheck = false, disableAddressCheck,
    onUpdate,
  } = props;

  const { t } = useTranslation();
  const { includeVAT } = useContext(OrderContext);

  const [fullAddress, setFullAddress] = useState(streetName + (houseNumber ? ` ${houseNumber}` : ''));
  const [fullNumber, setFullNumber] = useState(houseLetter + (residence ? ` ${residence}` : ''));

  const [addressFocus, setAddressFocus] = useState(false);
  const [houseLetterFocus, setHouseLetterFocus] = useState(false);
  const [residenceFocus, setResidenceFocus] = useState(false);

  const [loading, setLoading] = useState(false);
  const [addressStatus, setAddressStatus] = useState<AddressStatus | undefined>(status);
  const [selectedPostalCodeArea, setSelectedPostalCodeArea] = useState<PostalCodeArea>();
  const [streetNames, setStreetNames] = useState<Street[]>([]);
  const [filteredStreets, setFilteredStreets] = useState<Street[]>([]);
  const [houseNumbers, sethouseNumbers] = useState<string[]>([]);
  const [filteredNumbers, setFilteredNumbers] = useState<string[]>([]);
  const [filteredHouseLetters, setFilteredHouseLetters] = useState<string[]>([]);
  const [activeHouseLetter, setActiveHouseLetter] = useState<string | undefined>(undefined);
  const [filteredResidences, setFilteredResidences] = useState<string[]>();
  const [activeResidence, setActiveResidence] = useState<string | undefined>(undefined);

  const [payload, setPayload] = useState<AddressPayload>({
    zipcode: undefined,
    streetname: undefined,
    streetnumber: undefined,
    city: undefined,
    floornumber: undefined,
    roomnumber: undefined,
  });

  useEffect(() => {
    onUpdate(undefined, addressStatus);
  }, [addressStatus])

  useEffect(() => {

    if (!zipCode || !streetName || !houseNumber) {
      return;
    }

    const handler = setTimeout(() => {
      if (filteredHouseLetters.length < 1 && (!filteredResidences || (filteredResidences && filteredResidences.length < 1))) {
        checkAddress();
      }
    }, 1000);

    return () => {
      clearTimeout(handler);
    };

  }, [zipCode, streetName, houseNumber, cityName, houseLetter, residence]);

  useEffect(() => {
    if (zipCode.length == 5 && /^\d{5}$/.test(zipCode)) {
      getPostalCodeArea(zipCode).then((pCode) => {
        if (pCode == undefined || !pCode.streets || pCode.streets.length === 0) {
          return;
        }

        setStreetNames(pCode.streets);
        setSelectedPostalCodeArea(pCode);
      });
    } else {
      setSelectedPostalCodeArea(undefined);
      setStreetNames([]);
      sethouseNumbers([]);
      setStreetNames([]);
    }
  }, [zipCode]);

  useEffect(() => {
    const address: Partial<AddressType> = {
      cityName: selectedPostalCodeArea?.city,
    }
    onUpdate(address);
    setAddressStatus(undefined);
  }, [selectedPostalCodeArea]);

  useEffect(() => {
    const postalC = zipCode;
    const streetN = streetName;
    if (postalC && streetN) {
      const streets = getStreetNamesWithHouseNumbers(postalC, streetN);
      setStreetNames(streets);
    } else {
      setStreetNames([]);
    }

  }, [streetName, zipCode]);

  useEffect(() => {
    setFilteredStreets(streetNames);
  }, [streetNames]);

  useEffect(() => {
    let value = ''
    if (!houseNumber) {
      value = streetName
    } else {
      value = `${streetName} ${houseNumber}`
    }
    setFullAddress(value);
  }, [streetName, houseNumber]);

  useEffect(() => {
    let value = '';
    if (!residence) {
      value = houseLetter;
    } else {
      value = `${houseLetter} ${residence}`;
    }
    setFullNumber(value);
  }, [houseLetter, residence]);

  const handleFullAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFullAddress(value);

    filterStreets(value)
  }

  useEffect(() => {
    const parts = fullAddress.trim().split(" ");

    let name = "";
    let number = "";

    // Assume last part is the number(if it contais digits) and the rest is the street name
    if (parts.length > 0) {
      const last = parts[parts.length - 1];
      if (last.match(/^\d+/)) {
        number = last;
        name = parts.slice(0, parts.length - 1).join(" ");
      } else {
        name = parts.join(" ");
      }
    }

    const address: Partial<AddressType> = {
      streetName: name,
      houseNumber: number,
    }
    onUpdate(address);
    setAddressStatus(undefined);
    sethouseNumbers([]);
  }, [fullAddress]);

  useEffect(() => {
    setAddressStatus(undefined);
    filterNumbers(fullNumber);
  }, [fullNumber]);

  useEffect(() => {
    let data = houseNumbers;
    if (filteredNumbers.length > 0) {
      data = filteredNumbers;
    }
    const letters = Array.from(new Set(data.sort((a, b) => a.localeCompare(b)).map((number) => {
      const parts = number.split(" ");
      return parts.length > 1 ? parts[0] : number;
    })));
    setFilteredHouseLetters(letters);

    const residences = Array.from(new Set(data.map((number) => {
      const parts = number.split(" ");
      return parts.length > 1 ? parts.slice(1).join(" ") : '';
    }))).filter((res) => res !== '');
    setFilteredResidences(residences);

  }, [houseNumbers, filteredNumbers]);

  const filterNumbers = (value: string) => {
    let data = houseNumbers;
    if (value.length > 0) {
      data = houseNumbers.filter((number) => {
        return number.toLocaleLowerCase().includes(value.toLocaleLowerCase());
      });
    }
    setFilteredNumbers(data);

  }

  const filterStreets = (value: string) => {
    if (value.length > 0 && streetNames) {
      const exact = streetNames.find((street) => {
        return street.street.toLowerCase() === value.toLowerCase() && (street.minRange || street.maxRange);
      });
      const filtered = streetNames.filter((street) => {
        return street.street.toLowerCase().includes(value.toLowerCase());
      });
      if (exact) {
        setFilteredStreets([exact]);
      } else {
        setFilteredStreets(filtered);
      }
    }
  }

  const getStreetNamesWithHouseNumbers = (zipCode: string, streetName: string) => {
    /**
     *  Jos postinumero on annettu, hae kadut postinumeron perusteella
     *  Jos kadunnimi on annettu ja se tästää johonkin katuun postinumeroalueella, hae kadun numerot kadun perusteella
     *
     *
     *
     *  Return street names and possible numbers if street name is given and it matches to a street in the postal code area
     *  zipCode only: ['streetname', 'streetname']
     *  zipCode and streetname matches with area streetnames: ['streetname 1', 'streetname 2']
     */
    let streets: Street[] = [];
    if (selectedPostalCodeArea && selectedPostalCodeArea.postalCode === zipCode) {
      streets = selectedPostalCodeArea.streets;
      const exactStreet = streets.find((street: Street) => street.street.toLocaleLowerCase() === streetName.toLocaleLowerCase() && (street.minRange || street.maxRange));
      const filteredStreets = streets.filter((street: Street) => street.street?.toLowerCase().includes(streetName?.toLowerCase()));
      if (filteredStreets.length === 1 || exactStreet) {

        const street = exactStreet ?? filteredStreets[0];
        const numbers = [street.minRange, street.maxRange].filter((n) => n !== 0);
        const min = Math.min(...numbers);
        const max = Math.max(...numbers);
        streets = [];
        for (let i = min; i <= max; i++) {
          streets.push({ street: `${street.street} ${i}`, minRange: 0, maxRange: 0 });
        }
      }
    }
    return streets;
  }

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const address: Partial<AddressType> = {
      [name as keyof AddressType]: value,
    }

    if (name === 'houseLetter' && !value) {
      address.residence = '';
    }

    onUpdate(address);
    setAddressStatus(undefined);
  }

  const checkAddress = async (force = false) => {
    if (disableAddressCheck) {
      return
    }
    if (force == false && houseNumbers.length > 0 && addressStatus !== 'confirmed') {
      return;
    }

    const body: AddressPayload = {
      zipcode: zipCode,
      streetname: streetName,
      streetnumber: houseNumber,
      city: cityName,
      floornumber: houseLetter,
      roomnumber: residence,
    }
    if (force === false && payload.zipcode === body.zipcode &&
      payload.streetname === body.streetname &&
      payload.streetnumber === body.streetnumber &&
      payload.city === body.city &&
      payload.floornumber === body.floornumber &&
      payload.roomnumber === body.roomnumber) {
      return;
    }
    setLoading(true);
    setPayload(body);

    const url = '/_hcms/api/address/check';

    const response: MeteringPointCheckResponse[] = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then((response) => {
        setLoading(false);
        return response.json()
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('Error:', error);
        setLoading(false);
        return [];
      });

    const data = response[0];

    let status: AddressStatus = 'unconfirmed';

    if (data && data.success) {
      const points = data.meteringPoints;
      handleFinalAddresses(points);
      if (points.length === 1) {
        status = 'confirmed';
      }
    } else {
      sethouseNumbers([]);
    }
    if (force || addressStatus === 'confirmed' || (addressStatus === 'skipped' && status === 'unconfirmed')) {
      setAddressStatus(status);
    }
    setLoading(false);
  };

  const handleFinalAddresses = (rows: MeteringPointCheckObject[]) => {
    const filtered = rows.filter(row => (row.floorIdentification || row.roomIdentification) && row.streetName.toLowerCase() === streetName.toLowerCase() && row.buildingNumber === houseNumber)
    const numbers = filtered
      .map((point: any) => {
        let result = '';
        if (point.floorIdentification) {
          result += point.floorIdentification;
        }
        if (point.roomIdentification) {
          result += " " + point.roomIdentification;
        }
        return result;
      });
    sethouseNumbers(numbers);
  }

  const getPostalCodeArea = async (zipCode: string): Promise<PostalCodeArea | undefined> => {
    if (selectedPostalCodeArea?.postalCode === zipCode) {
      return selectedPostalCodeArea;
    }
    setLoading(true);
    const url = `/_hcms/api/address/search?postalcode=${zipCode}`;
    const response = await fetch(url)
      .then((response) => {
        setLoading(false);
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('Error:', error);
        setLoading(false);
      });

    return response;
  }

  const handleAddressKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault(); // Prevent page scrolling

      const index = filteredStreets.findIndex((street) => street.street === fullAddress);
      let newIndex = index;

      if (e.key === 'ArrowDown') {
        newIndex = index < filteredStreets.length - 1 ? index + 1 : 0;
      } else if (e.key === 'ArrowUp') {
        newIndex = index > 0 ? index - 1 : filteredStreets.length - 1;
      }

      setFullAddress(filteredStreets[newIndex].street);

      setTimeout(() => {
        const container = document.querySelector('.autocomplete-container');
        const element = document.getElementById(filteredStreets[newIndex].street);

        if (container && element) {
          const containerRect = container.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();

          if (elementRect.top < containerRect.top) {
            container.scrollTop -= containerRect.top - elementRect.top;
          } else if (elementRect.bottom > containerRect.bottom) {
            container.scrollTop += elementRect.bottom - containerRect.bottom;
          }
        }
      }, 50);
    }

    if (e.key === 'Enter') {
      setAddressFocus(false);
    }
  };

  const handleHouseLetterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault(); // Prevent page scrolling
      const index = filteredHouseLetters.findIndex((letter) => letter === activeHouseLetter);
      let newIndex = index;
      if (e.key === 'ArrowDown') {
        newIndex = index < filteredHouseLetters.length - 1 ? index + 1 : 0;
      } else if (e.key === 'ArrowUp') {
        newIndex = index > 0 ? index - 1 : filteredHouseLetters.length - 1;
      }
      setActiveHouseLetter(filteredHouseLetters[newIndex]);

      setTimeout(() => {
        const container = document.querySelector('.autocomplete-container');
        const element = document.getElementById(filteredHouseLetters[newIndex]);

        if (container && element) {
          const containerRect = container.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();

          if (elementRect.top < containerRect.top) {
            container.scrollTop -= containerRect.top - elementRect.top;
          } else if (elementRect.bottom > containerRect.bottom) {
            container.scrollTop += elementRect.bottom - containerRect.bottom;
          }
        }
      }, 50);
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      setHouseLetterFocus(false);
      selectHouseLetter(activeHouseLetter || '');
      setActiveHouseLetter(undefined);
    }
  };

  const handleResidenceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!filteredResidences || filteredResidences.length === 0) {
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault(); // Prevent page scrolling
      const index = filteredResidences.findIndex((res) => res === activeResidence);
      let newIndex = index;
      if (e.key === 'ArrowDown') {
        newIndex = index < filteredResidences.length - 1 ? index + 1 : 0;
      } else if (e.key === 'ArrowUp') {
        newIndex = index > 0 ? index - 1 : filteredResidences.length - 1;
      }
      setActiveResidence(filteredResidences[newIndex]);
      setTimeout(() => {
        const container = document.querySelector('.autocomplete-container');
        const element = document.getElementById(filteredResidences[newIndex]);
        if (container && element) {
          const containerRect = container.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();

          if (elementRect.top < containerRect.top) {
            container.scrollTop -= containerRect.top - elementRect.top;
          } else if (elementRect.bottom > containerRect.bottom) {
            container.scrollTop += elementRect.bottom - containerRect.bottom;
          }
        }
      }, 50);
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      setResidenceFocus(false);
      selectResidenece(activeResidence || '');
      setActiveResidence(undefined);
    }
  }

  const handleAddressAutoCompleteClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const firstStreet = filteredStreets[0];
    // check if firstStreet.street contains a number with regex
    const number = firstStreet.street.match(/\d+/);
    if (number) return
    e.preventDefault();
  }

  const toTitleCase = (str: string) => {
    return str.replace(/\w\S*/g, function (txt) { return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(); });
  }

  const handleStreetName = (name: string) => {
    const words = name.split(" ");
    const newValue = words
      .map((word, index) => {
        if (index === words.length - 1 && ['tie', 'katu', 'aukio'].includes(word.toLowerCase())) {
          return word.toLowerCase();
        }
        return toTitleCase(word);
      })
      .join(" ");
    return newValue;
  }

  const handleAddressBlur = () => {
    setTimeout(() => setAddressFocus(false), 200)
    const address: Partial<AddressType> = {
      streetName: handleStreetName(streetName),
      houseNumber: houseNumber,
    }
    onUpdate(address);
  }

  const selectHouseLetter = (letter: string) => {
    handleOnChange({ target: { name: 'houseLetter', value: letter } } as React.ChangeEvent<HTMLInputElement>);
  }
  const selectResidenece = (res: string) => {
    handleOnChange({ target: { name: 'residence', value: res } } as React.ChangeEvent<HTMLInputElement>);
  }


  return (<>
    {!onlyAddressCheck && <Grid style={{ position: 'relative' }}>
      <div>
        <label>{t('address.zipcode')}*
          <input
            type="text"
            className={zipCodeError ? 'invalid' : ''}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            name="zipCode"
            value={zipCode}
            onChange={handleOnChange} />
          {zipCodeError && <span className="invalid">{zipCodeError}</span>}
        </label>
      </div>

      <div>
        <label>{t('address.city')}*
          <input
            type="text"
            className={cityNameError ? 'invalid' : ''}
            name="cityName"
            disabled={true}
            value={cityName}
            onChange={handleOnChange} />
          {cityNameError && <span className="invalid">{cityNameError}</span>}
        </label>
      </div>

      <div style={{ position: 'relative' }}>
        <label>{t('address.streetAndNumber')}

          <input
            type="text"
            name="fullAddress"
            className={streetNameError || houseNumberError ? 'invalid' : ''}
            autoComplete="off"
            value={fullAddress}
            onChange={handleFullAddressChange}
            onFocus={() => setAddressFocus(true)}
            onBlur={handleAddressBlur}
            onKeyDown={handleAddressKeyDown} />
          {(streetNameError || houseNumberError) && <span className="invalid">{streetNameError || houseNumberError}</span>}
          {addressFocus && filteredStreets && filteredStreets.length > 0 &&
            <AutoCompleteSelect onMouseDown={handleAddressAutoCompleteClick}>
              {filteredStreets.map((street, i) => (
                <div key={i} id={street.street} className={fullAddress == street.street ? 'active' : ''} onClick={() => setFullAddress(street.street)}>
                  <span>{street.street}</span>
                </div>
              ))}
            </AutoCompleteSelect>}

        </label>

      </div>
      <LetterAndResidence>
        <div>
          <label>{t('address.letter')}
            <input
              type="text"
              name="houseLetter"
              className={houseLetterError ? 'invalid' : ''}
              value={houseLetter}
              onChange={handleOnChange}
              onFocus={() => setHouseLetterFocus(true)}
              onKeyDown={handleHouseLetterKeyDown}
              onBlur={() => setTimeout(() => setHouseLetterFocus(false), 200)} />
            {houseLetterError && <span className="invalid">{houseLetterError}</span>}
            {houseLetterFocus && houseNumbers && filteredHouseLetters.length > 0 &&
              <AutoCompleteSelect>
                {filteredHouseLetters.map((letter, i) => (
                  <div key={i} id={letter} className={activeHouseLetter == letter ? 'active' : ''} onClick={() => selectHouseLetter(letter)}>
                    <span>{letter}</span>
                  </div>
                ))}
              </AutoCompleteSelect>}
          </label>
        </div>
        <div>
          <label>{t('address.residence')}
            <input
              type="text"
              name="residence"
              className={residenceError ? 'invalid' : ''}
              value={residence}
              disabled={!houseLetter}
              onChange={handleOnChange}
              onFocus={() => setResidenceFocus(true)}
              onKeyDown={handleResidenceKeyDown}
              onBlur={() => setTimeout(() => setResidenceFocus(false), 200)} />
            {residenceError && <span className="invalid">{residenceError}</span>}
            {residenceFocus && filteredResidences && filteredResidences.length > 0 &&
              <AutoCompleteSelect>
                {filteredResidences?.map((res, i) => (
                  <div key={i} id={res} className={activeResidence == res ? 'active' : ''} onClick={() => selectResidenece(res)}>
                    <span>{res}</span>
                  </div>
                ))}
              </AutoCompleteSelect>}
          </label>
        </div>
      </LetterAndResidence>

      {!hidecareOf && <div>
        <TextInput label={t('address.coName')} fieldName="careOf" value={careOf} onChange={handleOnChange} />
      </div>}

    </Grid>}

    {!disableAddressCheck && <div style={{ marginTop: '20px' }}>
      {!addressStatus && statusError && <span className="invalid">{t('address.confirm')}</span>}
      {addressStatus && <StatusBox>
        {addressStatus === 'unconfirmed' && <div className={"not-found" + (statusError ? ' invalid' : '')}>
          <img src={iconAlertCircle} />
          <div className="content">
            <div className="title">{t('address.notFound')}</div>
            <div className="description">{t('address.notFoundDescription')}</div>
          </div>

        </div>}
        {addressStatus === 'skipped' && <div className="not-found">{t('address.confirmOverride')}</div>}
        {addressStatus === 'confirmed' && <span className="success"><img src={iconCheck} />{t('address.confirmed')}</span>}
      </StatusBox>}
      <Buttons>
        {addressStatus == undefined && <>
          <button type="button" className={"primary" + (includeVAT ? ' purple' : '')} onClick={() => checkAddress(true)}>{t('address.confirm')}</button>
        </>}
        {(addressStatus === 'unconfirmed') && <>
          <button type="button" className="green" onClick={() => setAddressStatus('skipped')}>{t('address.confirmSkip')}</button>
        </>}
      </Buttons>
    </div>}

    {loading && <div className="overlay"><LoadingSpinner /></div>}

  </>


  );
}
export default Address;
